import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, Package, Plane, Ship, Zap, ArrowRight, ShieldCheck, CheckCircle2, AlertCircle, MapPin, ExternalLink, Box, Globe, Truck, Landmark, Check } from 'lucide-react';
import { PackageItem } from '../types';
import { calculateShippingCosts, getRedirectionFeeUsd, DEFAULT_CURRENCY_RATES, isPackageFreeShipping, ShippingCostCalculation } from '../lib/shippingAndPayment';
import { CourierQuoteModal } from './CourierQuoteModal';
import { ShippingSafetyMarginSuggestion } from './ShippingSafetyMarginSuggestion';
import { ImportTaxSimulator } from './ImportTaxSimulator';
import { DESTINATION_COUNTRIES, getCountryInfo } from '../lib/countries';

interface ShippingCalculatorProps {
  userPackages?: PackageItem[];
  onStartConsolidation?: (selectedPackageIds: string[]) => void;
  isAdmin?: boolean;
}

const BRAZIL_STATES = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 
  'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'
];

export const ShippingCalculator: React.FC<ShippingCalculatorProps> = ({
  userPackages = [],
  onStartConsolidation,
  isAdmin = false
}) => {
  const [calcMode, setCalcMode] = useState<'packages' | 'custom'>('packages');
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>(
    userPackages.filter(p => p.status === 'Recebido').map(p => p.id)
  );
  const [customWeightKg, setCustomWeightKg] = useState<number>(1.5);
  const [customLength, setCustomLength] = useState<number>(30);
  const [customWidth, setCustomWidth] = useState<number>(20);
  const [customHeight, setCustomHeight] = useState<number>(15);
  const [destinationCountry, setDestinationCountry] = useState<string>('Brasil');
  const [destinationState, setDestinationState] = useState<string>('SP');
  const [selectedMethodId, setSelectedMethodId] = useState<string>('ems');
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [, setRateVersion] = useState(0);

  useEffect(() => {
    const handleUpdate = () => setRateVersion(v => v + 1);
    window.addEventListener('currencyRatesUpdated', handleUpdate);
    return () => window.removeEventListener('currencyRatesUpdated', handleUpdate);
  }, []);

  const countryInfo = getCountryInfo(destinationCountry);

  // Compute active weight
  const activeWeightKg = calcMode === 'packages'
    ? userPackages.filter(p => selectedPackageIds.includes(p.id)).reduce((acc, curr) => acc + curr.weightKg, 0)
    : customWeightKg;

  const selectedPackagesList = userPackages.filter(p => selectedPackageIds.includes(p.id));
  const isAllFreeShipping = calcMode === 'packages' && selectedPackagesList.length > 0 && selectedPackagesList.every(isPackageFreeShipping);

  const results = calculateShippingCosts(
    activeWeightKg, 
    1, 
    'free', 
    destinationCountry, 
    isAllFreeShipping,
    calcMode === 'custom' ? customLength : 0,
    calcMode === 'custom' ? customWidth : 0,
    calcMode === 'custom' ? customHeight : 0
  );

  const togglePackage = (id: string) => {
    const targetPkg = userPackages.find(p => p.id === id);
    if (!targetPkg) return;

    setSelectedPackageIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      }
      const currentlySelected = userPackages.filter(p => prev.includes(p.id));
      if (currentlySelected.length > 0) {
        const currentAreFree = currentlySelected.every(isPackageFreeShipping);
        const targetIsFree = isPackageFreeShipping(targetPkg);
        if (currentAreFree !== targetIsFree) {
          alert(targetIsFree 
            ? '⚠️ Este produto possui FRETE GRÁTIS INTERNACIONAL.\n\nProdutos com Frete Grátis devem ser consolidados separadamente de produtos com Frete Normal.' 
            : '⚠️ Os produtos selecionados possuem FRETE GRÁTIS.\n\nProdutos com Frete Grátis não podem ser misturados com produtos de Frete Normal.'
          );
          return prev;
        }
      }
      return [...prev, id];
    });
  };

  // Active selected shipping method calculation for tax simulation
  const selectedShippingCalc = useMemo(() => {
    return results.find(r => r.method.id === selectedMethodId && r.isEligible) || 
           results.find(r => r.isEligible && !r.method.isTemporarilyUnavailable) || 
           results[0];
  }, [results, selectedMethodId]);

  const packagesDeclaredValueYen = calcMode === 'packages' 
    ? selectedPackagesList.reduce((acc, p) => acc + (p.declaredValue || 0), 0)
    : 0;

  return (
    <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 space-y-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#1D3557] text-white rounded-2xl shadow-md shadow-blue-900/10">
            <Calculator className="w-6 h-6 text-[#E63946]" />
          </div>
          <div>
            <h3 className="text-xl font-black text-[#1D3557] flex items-center gap-2">
              <span>Calculadora de Frete Japão → {countryInfo.flag} {countryInfo.name}</span>
            </h3>
            <p className="text-slate-500 text-xs sm:text-sm flex items-center gap-2">
              <span>Simule o valor do envio em tempo real ({countryInfo.shippingZone})</span>
            </p>
          </div>
        </div>

        {/* Mode Selector */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl w-full sm:w-auto">
          <button
            onClick={() => setCalcMode('packages')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-1 sm:flex-none justify-center ${
              calcMode === 'packages' ? 'bg-[#1D3557] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Package className="w-3.5 h-3.5" />
            <span>Pacotes da Suíte ({userPackages.filter(p => p.status === 'Recebido').length})</span>
          </button>
          <button
            onClick={() => setCalcMode('custom')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-1 sm:flex-none justify-center ${
              calcMode === 'custom' ? 'bg-[#1D3557] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Calculator className="w-3.5 h-3.5" />
            <span>Simular Peso Manual</span>
          </button>
        </div>
      </div>

      {/* Input Selection Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Weight Source Inputs */}
        <div className="lg:col-span-1 space-y-4 bg-slate-50 p-4 rounded-2xl border border-slate-200/80">
          
          {calcMode === 'packages' ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Selecione os Pacotes</span>
                <span className="text-xs font-extrabold text-[#E63946]">
                  {selectedPackageIds.length} Selecionados
                </span>
              </div>

              {userPackages.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nenhum pacote recebido na suíte para calcular.</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {userPackages.map((pkg) => (
                    <label
                      key={pkg.id}
                      onClick={() => togglePackage(pkg.id)}
                      className={`p-2.5 rounded-xl border text-xs flex items-center justify-between cursor-pointer transition-all ${
                        selectedPackageIds.includes(pkg.id)
                          ? 'bg-blue-50/80 border-[#1D3557] text-[#1D3557] font-bold shadow-2xs'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate pr-2">
                        <input
                          type="checkbox"
                          checked={selectedPackageIds.includes(pkg.id)}
                          onChange={() => {}}
                          className="rounded text-[#1D3557] focus:ring-0"
                        />
                        <span className="truncate">{pkg.description}</span>
                        {isPackageFreeShipping(pkg) && (
                          <span className="px-1.5 py-0.5 bg-emerald-600 text-white text-[9px] font-black rounded uppercase flex-shrink-0">
                            Frete Grátis
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-slate-900 font-extrabold flex-shrink-0">
                        {pkg.weightKg.toFixed(2)} kg
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Digite o Peso Total Estimado (kg)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="50"
                    value={customWeightKg}
                    onChange={(e) => setCustomWeightKg(parseFloat(e.target.value) || 0.1)}
                    className="w-full px-4 py-3 bg-white rounded-xl border border-slate-300 font-mono font-extrabold text-slate-900 text-lg focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    KG ({ (customWeightKg * 2.20462).toFixed(2) } LB)
                  </span>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-200">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Dimensões da Caixa (cm)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[10px] text-slate-500 font-bold mb-1">Comp. (C)</label>
                    <input
                      type="number"
                      min="1"
                      value={customLength}
                      onChange={(e) => setCustomLength(parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-2 bg-white rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 font-bold mb-1">Largura (L)</label>
                    <input
                      type="number"
                      min="1"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-2 bg-white rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-slate-500 font-bold mb-1">Altura (A)</label>
                    <input
                      type="number"
                      min="1"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-2 bg-white rounded-lg border border-slate-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">
                  O cálculo FedEx usará o peso cubado se for maior que o real.
                </p>
              </div>
            </div>
          )}

          {/* Destination Selector */}
          <div className="space-y-3 pt-2 border-t border-slate-200/80">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-blue-600" />
                <span>País de Destino Final</span>
              </label>
              <select
                value={destinationCountry}
                onChange={(e) => setDestinationCountry(e.target.value)}
                className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
              >
                {DESTINATION_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.flag} {c.name}
                  </option>
                ))}
              </select>
            </div>

            {destinationCountry === 'Brasil' && (
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Estado de Destino no Brasil
                </label>
                <select
                  value={destinationState}
                  onChange={(e) => setDestinationState(e.target.value)}
                  className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1D3557]"
                >
                  {BRAZIL_STATES.map((st) => (
                    <option key={st} value={st}>
                      {st} — Brasil
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Active Weight & Redirection Fee Summary Card */}
          <div className="p-3 bg-[#1D3557] text-white rounded-xl space-y-2 text-center">
            <div>
              <span className="text-[10px] uppercase font-bold text-slate-300 tracking-wider block">Peso Total Combinado</span>
              <div className="text-xl font-black font-mono text-white">
                {activeWeightKg.toFixed(2)} kg <span className="text-xs text-red-200">({ (activeWeightKg * 2.20462).toFixed(2) } lb)</span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-700/80 text-xs">
              <span className="text-[10px] text-sky-200 font-bold uppercase tracking-wider block mb-1">Tabela de Taxa de Serviço por Caixa</span>
              <div className="grid grid-cols-3 gap-1 text-[11px] font-bold">
                <div className={`p-1.5 rounded-lg border ${activeWeightKg <= 1.0 ? 'bg-amber-400 text-slate-950 border-amber-300 font-black' : 'bg-slate-800/80 text-slate-300 border-slate-700'}`}>
                  Até 1kg<br/><span className="text-[10px]">$15,00 USD</span>
                </div>
                <div className={`p-1.5 rounded-lg border ${activeWeightKg > 1.0 && activeWeightKg <= 2.0 ? 'bg-amber-400 text-slate-950 border-amber-300 font-black' : 'bg-slate-800/80 text-slate-300 border-slate-700'}`}>
                  Até 2kg<br/><span className="text-[10px]">$20,00 USD</span>
                </div>
                <div className={`p-1.5 rounded-lg border ${activeWeightKg > 2.0 ? 'bg-amber-400 text-slate-950 border-amber-300 font-black' : 'bg-slate-800/80 text-slate-300 border-slate-700'}`}>
                  Acima 2kg<br/><span className="text-[10px]">$30,00 USD</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Right Column: Calculated Shipping Rates */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Automatic Safety Margin Suggestion Component */}
          <ShippingSafetyMarginSuggestion
            netWeightKg={activeWeightKg}
            destinationCountry={destinationCountry}
            onApplySuggestedWeight={(suggestedGross) => {
              setCalcMode('custom');
              setCustomWeightKg(suggestedGross);
            }}
            isAdmin={isAdmin}
          />

          <div className="flex justify-between items-center text-xs text-slate-500 font-bold uppercase tracking-wider px-1">
            <span>Modalidades de Envio Disponíveis</span>
            <span>Taxa 7% Incluída no BRL</span>
          </div>

          <div className="space-y-3">
            {results.map((res) => {
              const isSelected = selectedMethodId === res.method.id;
              return (
                <div
                  key={res.method.id}
                  onClick={() => {
                    if (res.isEligible && !res.method.isTemporarilyUnavailable) {
                      setSelectedMethodId(res.method.id);
                    }
                  }}
                  className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                    isSelected && res.isEligible
                      ? 'bg-blue-50/40 border-[#1D3557] ring-2 ring-[#1D3557]/20 shadow-md'
                      : res.isEligible
                        ? 'bg-white border-slate-200/90 hover:border-slate-400 hover:shadow-xs'
                        : 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    
                    {/* Method Name & Duration */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {/* Radio Check Indicator */}
                        {res.isEligible && !res.method.isTemporarilyUnavailable && (
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                            isSelected 
                              ? 'border-[#1D3557] bg-[#1D3557] text-white' 
                              : 'border-slate-300 bg-white'
                          }`}>
                            {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                          </div>
                        )}

                        {res.method.type === 'ems' && <Zap className="w-4 h-4 text-amber-500" />}
                        {res.method.type === 'small_packet' && <Plane className="w-4 h-4 text-blue-500" />}
                        {res.method.type === 'surface' && <Ship className="w-4 h-4 text-emerald-600" />}
                        {res.method.type === 'sal' && <Plane className="w-4 h-4 text-sky-500" />}
                        {res.method.type === 'fedex' && <Zap className="w-4 h-4 text-purple-600" />}
                        {res.method.type === 'ups' && <Truck className="w-4 h-4 text-yellow-600" />}
                        {res.method.type === 'dhl' && <Zap className="w-4 h-4 text-red-600" />}

                        <h4 className="font-extrabold text-sm text-[#1D3557]">{res.method.name}</h4>
                        
                        {res.method.type === 'ems' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full uppercase">
                            Mais Popular
                          </span>
                        )}

                        {res.method.type === 'ems' && (
                          <a
                            href="https://www.post.japanpost.jp/send/oversea/charge/list-ems/zone5_en.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
                            title="Consultar tabela oficial de tarifas EMS Zone 5 dos Correios do Japão (+25% taxa depósito)"
                          >
                            <span>Japan Post Zone 5</span>
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}

                        {res.method.isTemporarilyUnavailable && (
                          <span className="px-2 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-black rounded-full uppercase">
                            Indisponível p/ Brasil
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-500">{res.method.description}</p>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        <span>Prazo estimado: <strong className="text-slate-800">{res.method.estimatedDays}</strong></span>
                        <span>•</span>
                        <span>Até {res.method.maxWeightKg} kg</span>
                        {isSelected && res.isEligible && (
                          <span className="text-sky-700 font-extrabold bg-sky-100 px-2 py-0.5 rounded-md text-[10px]">
                            ✓ Vinculado ao Simulador de Impostos
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    {res.requiresQuote || res.method.requiresQuote ? (
                      <div className="text-left sm:text-right flex-shrink-0 bg-purple-50/80 p-3 rounded-xl border border-purple-100 space-y-2" onClick={(e) => e.stopPropagation()}>
                        <div>
                          <span className="px-2 py-0.5 bg-purple-200 text-purple-900 text-[10px] font-black rounded-full uppercase tracking-wider">
                            Sob Orçamento
                          </span>
                          <div className="text-xs font-bold text-purple-950 mt-1">
                            Cotação personalizada por CEP
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsQuoteModalOpen(true)}
                          className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-800 hover:to-indigo-800 active:scale-95 text-white font-extrabold text-xs rounded-xl shadow-xs transition-all flex items-center justify-center gap-1.5"
                        >
                          <MapPin className="w-3.5 h-3.5 text-amber-300" />
                          <span>Pedir Orçamento (Informar CEP)</span>
                        </button>
                      </div>
                    ) : isAllFreeShipping ? (
                      <div className="text-left sm:text-right flex-shrink-0 bg-emerald-50 p-3 rounded-xl border border-emerald-200 space-y-0.5">
                        <div className="text-xs text-emerald-700 font-extrabold flex items-center gap-1 justify-end">
                          <Truck className="w-3.5 h-3.5 text-emerald-600" />
                          <span>FRETE GRÁTIS INCLUSO</span>
                        </div>
                        <div className="text-lg font-black text-emerald-600 font-mono pt-0.5">
                          FRETE GRÁTIS
                        </div>
                        <div className="text-[10px] font-bold text-emerald-700">
                          (R$ 0,00 — Despacho Sem Custo)
                        </div>
                      </div>
                    ) : res.isEligible ? (
                      <div className="text-left sm:text-right flex-shrink-0 bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-0.5">
                        <div className="text-xs text-slate-500 font-mono">
                          Frete: ¥ {res.costJpy.toLocaleString('ja-JP')} (~US$ {res.costUsd.toFixed(2)})
                        </div>
                        <div className="text-xs font-bold text-sky-800">
                          + Redirecionamento: US$ {res.redirectionFeeUsd.toFixed(2)}
                        </div>
                        <div className="text-lg font-black text-[#E63946] font-mono pt-1">
                          R$ {res.totalCostBrl.toFixed(2)}
                        </div>
                        <div className="text-[10px] font-semibold text-slate-500">
                          (Subtotal R$ {res.baseCostBrl.toFixed(2)} + 7% Tx)
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-red-500 font-bold flex items-center gap-1 bg-red-50 p-2 rounded-xl border border-red-100">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{res.ineligibilityReason}</span>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>

          {/* Action to proceed directly to consolidation */}
          {onStartConsolidation && calcMode === 'packages' && selectedPackageIds.length > 0 && (
            <div className="pt-2 flex justify-end">
              <button
                onClick={() => onStartConsolidation(selectedPackageIds)}
                className="px-6 py-3 bg-[#E63946] hover:bg-[#d62839] text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center gap-2"
              >
                <span>Solicitar Consolidação com os Pacotes Selecionados</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </div>

      </div>

      {/* Import Taxes Simulator Section (II + ICMS) */}
      <div className="pt-4 border-t border-slate-200/90">
        <ImportTaxSimulator
          initialItemsValueJpy={packagesDeclaredValueYen > 0 ? packagesDeclaredValueYen : 10000}
          initialPackageWeightKg={activeWeightKg}
          selectedShippingMethod={selectedShippingCalc}
          destinationState={destinationState}
          onStateChange={(st) => setDestinationState(st)}
        />
      </div>

      <CourierQuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        initialWeightKg={activeWeightKg}
        initialState={destinationState}
      />

    </div>
  );
};

export default ShippingCalculator;
